import React, {
  useCallback,
  useState
} from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import useEmblaCarousel from 'embla-carousel-react'
import { useQuery } from 'react-apollo'
import classnames from 'classnames'

import HotspotSlide from '../../hotspot/hotspot-slide'
import useAutoplay from '../components/emblaCarouselAutoplay'
import { DotButton, useDotButton } from '../components/emblaCarouselDotButton'
import HOTSPOT_SLIDE_FRAGMENT from '../../../../graphql/fragments/hotspot-slide.gql'
import PROFILE_PREFERENCES_QUERY from '../../../../graphql/queries/profile-preferences.gql'
import { useIsAuthenticated } from '../../../hooks/use-is-authenticated'
import styles from './emblaCarouselForHotspot.css'

/**
 * Carousel component for Hotspot carousels.
 *
 * Props:
 *   - slides: Array of hotspot slide objects
 *   - options: Embla carousel configuration (loop, align, containScroll, duration)
 *   - plugins: Array of Embla plugin instances (e.g. Autoplay, Fade, WheelGestures)
 *   - slideInfo: Contains UI callbacks (isContentItemOnMyList, addToMyList),
 *                display flags (hasTopgradient, isGuestHome), and rental info
 *
 * Renders full-screen HotspotSlide items with:
 *   - Autoplay that pauses when a trailer loads and resumes when it ends
 *   - Dot navigation (shown only when there are multiple slides)
 *   - Trailer mute state shared across all slides
 */

const EmblaCarouselForHotspot = (props) => {
  const {
    slides = [], options, slideInfo, plugins
  } = props
  const {
    isContentItemOnMyList,
    addToMyList,
    hasTopgradient,
    isGuestHome,
    rentalInfo = []
  } = slideInfo
  const [isTrailerMuted, setIsTrailerMuted] = useState(true)
  const isAuthenticated = useIsAuthenticated()
  const profilePreferencesQuery = useQuery(PROFILE_PREFERENCES_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated
  })
  const trailerAutoplayEnabled = profilePreferencesQuery?.data
    ?.profilePreferences?.trailerAutoplayOnBrowsePageEnabled
  const hasMultipleHotspotSlides = slides.length > 1
  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins)
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi)
  const isCurrentItemRental = rentalInfo[selectedIndex]
  const { startAutoplay, stopAutoplay, restartAutoplay } = useAutoplay(emblaApi)

  const handleDotButtonClick = useCallback((index) => {
    // Reset autoplay so the next interval starts fresh after manual navigation.
    restartAutoplay()
    onDotButtonClick(index)
  }, [onDotButtonClick, restartAutoplay])

  // Carousel autoplay should stop when trailer is loaded or interaction is active.
  const onTrailerLoaded = useCallback(() => {
    if (!emblaApi) return
    stopAutoplay()
  }, [emblaApi, stopAutoplay])

  // Resume carousel autoplay once trailer playback is completed.
  const onTrailerEnded = useCallback(() => {
    if (!emblaApi) return
    startAutoplay()
  }, [emblaApi, startAutoplay])

  const handleViewportFocus = useCallback((event) => {
    // React Modal restores focus to the element that opened it. If hotspot
    // autoplay has advanced, that element can be in an off-screen slide and
    // the browser scrolls the overflow-hidden viewport to reveal it.
    event.currentTarget.scrollLeft = 0
  }, [])

  return (
    <div className={styles.embla}>
      <div className={styles.emblaViewport} ref={emblaRef} onFocusCapture={handleViewportFocus}>
        <div className={styles.emblaContainer}>
          {(slides || []).map((slide, index) => {
            // Handle both image and trailer on hotspot slides
            return (
              <div className={styles.emblaHotspotSlide} key={slide.contentItem?.id}>
                <HotspotSlide
                  slide={slide}
                  isContentItemOnMyList={isContentItemOnMyList}
                  addToMyList={addToMyList}
                  hasTopgradient={hasTopgradient}
                  isGuestHome={isGuestHome}
                  isTrailerMuted={isTrailerMuted}
                  setTrailerMuted={setIsTrailerMuted}
                  onTrailerLoaded={onTrailerLoaded}
                  onTrailerEnded={onTrailerEnded}
                  isSlideSelected={index === selectedIndex}
                  trailerAutoplayEnabled={trailerAutoplayEnabled}
                  isAuthenticated={isAuthenticated}
                />
              </div>
            )
          })}
        </div>
      </div>

      <div className={styles.emblaControls}>
        {/* Dot controls are only used for hotspot carousels with multiple slides. */}
        { hasMultipleHotspotSlides && (
          <div className={styles.emblaDots}>
            {scrollSnaps.map((scrollSnap, index) => (
              <DotButton
                key={String(scrollSnap)}
                onClick={() => handleDotButtonClick(index)}
                className={classnames(styles.emblaDot, {
                  [styles.emblaDotSelected]: index === selectedIndex,
                  [styles.emblaDotRental]: index === selectedIndex && isCurrentItemRental
                })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

EmblaCarouselForHotspot.propTypes = {
  slides: PropTypes.arrayOf(propType(HOTSPOT_SLIDE_FRAGMENT)),
  options: PropTypes.shape({
    loop: PropTypes.bool,
    align: PropTypes.string,
    containScroll: PropTypes.string,
    duration: PropTypes.number
  }).isRequired,
  plugins: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object
  ])).isRequired,
  slideInfo: PropTypes.shape({
    carousel: PropTypes.shape({
      tiles: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
      subType: PropTypes.string,
      contentType: PropTypes.string,
      header: PropTypes.string
    }),
    isContentItemOnMyList: PropTypes.func.isRequired,
    addToMyList: PropTypes.func.isRequired,
    hasTopgradient: PropTypes.bool,
    isGuestHome: PropTypes.bool,
    rentalInfo: PropTypes.arrayOf(PropTypes.bool)
  }).isRequired
}

EmblaCarouselForHotspot.defaultProps = {
  slides: []
}

export default EmblaCarouselForHotspot
